import React from "react";
import PropTypes from "prop-types";
import { humanEUR } from "./format";

// Additive waterfall of the projected forecast: spent-to-date → base (run-rate) →
// trend → seasonality → other (residual) → projected. Renders nothing when the
// decomposition payload is absent (graceful fallback for cold-start / flag-off).
const ROWS = [
  { key: "spent_to_date", label: "Spent to date" },
  { key: "base", label: "Base (run-rate)" },
  { key: "trend", label: "Trend" },
  { key: "seasonality", label: "Seasonality" },
  { key: "residual", label: "Other" },
];

// data: { spent_to_date, base, trend, seasonality, residual, projected }
const ForecastWaterfall = ({ data, driverLensHref }) => {
  if (!data || data.projected == null) return null;
  const total = data.projected || 1;
  return (
    <div className="mt-2 text-xs text-text-2">
      <div className="mb-1 font-medium text-text">
        Why {humanEUR(data.projected)}?
      </div>
      {ROWS.map((r) => {
        const val = data[r.key];
        if (val == null) return null;
        const pct = Math.max(0, Math.min(100, (Math.abs(val) / total) * 100));
        return (
          <div key={r.key} className="flex items-center gap-2 py-0.5">
            <span className="w-28 shrink-0">{r.label}</span>
            <span className="h-2 flex-1 overflow-hidden rounded bg-surface-2">
              <span
                className={val < 0 ? "block h-full bg-warning" : "block h-full bg-primary"}
                style={{ width: `${pct}%` }}
              />
            </span>
            <span className="w-16 text-right tabular-nums">
              {(val < 0 ? "-" : "+") + humanEUR(Math.abs(val))}
            </span>
          </div>
        );
      })}
      <div className="mt-1 flex items-center gap-2 border-t border-border pt-1 font-medium text-text">
        <span className="w-28 shrink-0">Projected</span>
        <span className="flex-1" />
        <span className="w-16 text-right tabular-nums">{humanEUR(data.projected)}</span>
      </div>
      {driverLensHref ? (
        <a
          href={driverLensHref}
          className="mt-1 inline-block text-primary hover:text-primary-hover hover:underline"
        >
          See driver breakdown (Volume / Price) →
        </a>
      ) : null}
    </div>
  );
};

ForecastWaterfall.propTypes = {
  data: PropTypes.shape({
    spent_to_date: PropTypes.number,
    base: PropTypes.number,
    trend: PropTypes.number,
    seasonality: PropTypes.number,
    residual: PropTypes.number,
    projected: PropTypes.number,
  }),
  driverLensHref: PropTypes.string,
};

ForecastWaterfall.defaultProps = {
  data: null,
  driverLensHref: null,
};

export default ForecastWaterfall;
